import type {
  ExportAnnotation,
  ExportFormat,
  ExportHighlightAnnotation,
  ExportInkAnnotation,
} from '@mss_creator/shared';

/**
 * An annotation as a *gesture* produces it: everything but the id, which the
 * page mints when it commits.
 *
 * Spelled out per member rather than as `Omit<ExportAnnotation, 'id'>`, because
 * `Omit` over a discriminated union collapses it into one object type with the
 * union of every member's keys — which would let an ink stroke carry a
 * `lineIndex` and a highlight carry `points`, silently.
 */
export type ExportAnnotationDraft =
  | Omit<ExportInkAnnotation, 'id'>
  | Omit<ExportHighlightAnnotation, 'id'>;

/**
 * Annotation state for the export page — decisions A5-A7 (2026-08-25 doc).
 *
 * Session-only by decision A5: held in React state, lost on reload, never
 * written to the matrix. Persisting them was considered and rejected because it
 * raises an unanswered ownership question — are these one person's meeting
 * scribbles, or matrix content everyone sees? — which changes what an
 * annotation *is*, not merely where it lives.
 *
 * Modelled as past/present/future rather than a mutable list with an undo log,
 * so every operation is a pure snapshot swap. Annotations are small plain data
 * (a stroke is an anchor plus a few hundred numbers), which makes whole-list
 * snapshots by far the cheapest correct undo — no diffing, no inverse
 * operations to keep in sync with the forward ones.
 */
export interface AnnotationState {
  annotations: readonly ExportAnnotation[];
  /** Snapshots older than `annotations`, most recent last. */
  past: readonly (readonly ExportAnnotation[])[];
  /** Snapshots undone from, most recently undone last. */
  future: readonly (readonly ExportAnnotation[])[];
}

export const EMPTY_ANNOTATION_STATE: AnnotationState = {
  annotations: [],
  past: [],
  future: [],
};

/**
 * Commits a new present, pushing the old one onto the past. Clears `future`:
 * once you act after undoing, the branch you undid is unreachable, and keeping
 * it would let redo resurrect annotations from an abandoned timeline.
 */
function commit(state: AnnotationState, annotations: readonly ExportAnnotation[]): AnnotationState {
  return { annotations, past: [...state.past, state.annotations], future: [] };
}

export function addAnnotation(
  state: AnnotationState,
  annotation: ExportAnnotation,
): AnnotationState {
  return commit(state, [...state.annotations, annotation]);
}

/**
 * Removes one annotation by id — the « Gomme » tool. Needed alongside undo
 * because undo can only reach the most recent action: fixing a stroke made
 * twenty strokes ago would otherwise mean destroying everything after it.
 */
export function removeAnnotation(state: AnnotationState, id: string): AnnotationState {
  const remaining = state.annotations.filter((annotation) => annotation.id !== id);
  // Nothing matched: leave the history untouched rather than recording a
  // no-op step the user would then have to undo.
  if (remaining.length === state.annotations.length) return state;
  return commit(state, remaining);
}

/** « Tout effacer » — one step, so a single undo brings everything back. */
export function clearAllAnnotations(state: AnnotationState): AnnotationState {
  if (state.annotations.length === 0) return state;
  return commit(state, []);
}

export function canUndo(state: AnnotationState): boolean {
  return state.past.length > 0;
}

export function canRedo(state: AnnotationState): boolean {
  return state.future.length > 0;
}

export function undo(state: AnnotationState): AnnotationState {
  const previous = state.past.at(-1);
  if (previous === undefined) return state;
  return {
    annotations: previous,
    past: state.past.slice(0, -1),
    future: [...state.future, state.annotations],
  };
}

export function redo(state: AnnotationState): AnnotationState {
  const next = state.future.at(-1);
  if (next === undefined) return state;
  return {
    annotations: next,
    past: [...state.past, state.annotations],
    future: state.future.slice(0, -1),
  };
}

/** The annotations a format's export leaves out: none for PDF, the ink for HTML, all for Excel. */
function droppedBy(
  format: ExportFormat,
  annotations: readonly ExportAnnotation[],
): readonly ExportAnnotation[] {
  if (format === 'pdf') return [];
  // Decision X8: the continuous HTML document keeps the content-anchored
  // highlights but not the ink, whose projection needs PDF tile geometry.
  if (format === 'html') return annotations.filter((annotation) => annotation.kind === 'ink');
  // The excel request has no annotations field at all.
  return annotations;
}

/**
 * What switching format would drop from the export — decision A6, narrowed by
 * decision S6 (2026-08-27 doc) and by decision X8 (issue #12): `'all'` when
 * the target leaves every annotation out (Excel), `'ink'` when it keeps the
 * highlights but not the strokes (HTML), `null` when nothing more is lost
 * than the current format already drops — so returning to PDF, or going from
 * Excel to HTML, never asks.
 *
 * Annotating stays a PDF affordance (S6): the viewer is the only surface that
 * edits them. The stash the confirmation promises needs no code: the
 * annotations live in page state and each request carries what its format
 * keeps, so switching back restores them untouched. That is why this asks
 * rather than warns.
 */
export function annotationLossOnFormatSwitch(
  from: ExportFormat,
  to: ExportFormat,
  annotations: readonly ExportAnnotation[],
): 'all' | 'ink' | null {
  // The three formats drop nested sets (PDF ⊂ HTML ⊂ Excel), so comparing the
  // counts tells whether the target drops anything the source still kept.
  const lost = droppedBy(to, annotations).length;
  if (lost === 0 || lost <= droppedBy(from, annotations).length) return null;
  return to === 'excel' ? 'all' : 'ink';
}
